/**
 * Demo "live mode" — a genuinely authenticated session coexisting with the mocked data.
 *
 * The demo build already resolves the real production hosts (`apps/dashboard/.env.demo` sets
 * `REACT_APP_COBLI_ENV=prod`), so MSW is the only thing standing between the app and
 * `api.cobli.co`. This module keeps the real JWT under its own keys, deliberately **outside**
 * `cobli-access-token`: the standard keys keep holding the demo's fake JWT, so the shell, the fleet
 * switcher, permissions and every dataset stay mocked. Only the two callers that read from here —
 * Cobli IA and the Metabase embed — talk to the real backend.
 *
 * Storing the real token under the standard key would be shorter and is wrong for two reasons:
 * `handlers/session.ts` mocks `/account/session` and re-mints the fake JWT on every read
 * (`store/db.ts` → `issueFreshAccessToken`), which would overwrite the real token; and the whole app
 * would start identifying as the real fleet while displaying fictional vehicles.
 *
 * None of this exists outside the demo: every entry point goes through `isDemoLiveActive()`, which
 * returns `false` immediately when `REACT_APP_DEMO_MODE !== 'true'` — and that comparison is inlined
 * at build time, so the whole path dies in the production tree-shake.
 */

import Environment from '@cobliteam/react-environments';
import jwtDecode from 'jwt-decode';
import type { CobliJwtPayload } from '../account/cobler/TemporaryTokenManager';
import type { SessionAPI } from '../account/session/ApiTypes';
import { AuthToken } from '../account/session/AuthTokenKeys';

const STATE_KEY = 'cobli-demo-live-state';
const TOKEN_KEY = 'cobli-demo-live-token';
const SID_KEY = 'cobli-demo-live-sid';
const USER_KEY = 'cobli-demo-live-user';
const FLEETS_KEY = 'cobli-demo-live-fleets';
const FLEET_KEY = 'cobli-demo-live-fleet';

export const DEMO_LIVE_KEYS = [STATE_KEY, TOKEN_KEY, SID_KEY, USER_KEY, FLEETS_KEY, FLEET_KEY];

/**
 * Marks a request as "live mode" so MSW lets it through.
 *
 * A query param rather than a header: `/account/session` is mocked by the demo and needs both
 * answers — the fake one for the shell, the real one for this session — so the passthrough guard has
 * to tell the two apart on the very same URL. A custom header would enter the CORS preflight and
 * `api.cobli.co` does not list it in `access-control-allow-headers`, which would fail the request in
 * the browser. An unknown query param is ignored by the backend and invisible to the preflight.
 */
export const DEMO_LIVE_PARAM = '_live';

export type DemoLiveState = 'pending' | 'active';

export interface DemoLiveFleet {
  id: string;
  name: string;
}

export interface DemoLiveUser {
  email: string;
  fullname: string;
}

/**
 * Exactly the same shape used by the other call sites in the repo (`IframeResizer`, `AppLayout`,
 * `DemoBanner`): `vite-plugin-environment` replaces the literal `process.env.REACT_APP_*` expression
 * at build time. Writing `process.env?.REACT_APP_DEMO_MODE` does not match that replacement, leaving
 * `process` undefined in the browser and making this return `false` forever, silently.
 */
const isDemoBuild = (): boolean => process.env.REACT_APP_DEMO_MODE === 'true';

/**
 * Live-mode debug channel, visible in the browser console.
 *
 * Deliberately the single point of access to `console`: it confines the `noConsole` exception to one
 * line and keeps the prefix and styling consistent between the lifecycle (this file) and the
 * per-request passthrough decisions (`libs/msw-demo/src/handlers/demoLive.ts`). Silent outside the
 * demo build.
 *
 * Filter the console by `[demo-live]` to see only this flow.
 */
export const logDemoLive = (message: string, ...data: unknown[]): void => {
  if (!isDemoBuild()) return;
  // biome-ignore lint/suspicious/noConsole: live-mode debug channel, explicitly requested
  console.info(`%c[demo-live]%c ${message}`, 'color:#0f9d58;font-weight:bold', 'color:inherit', ...data);
};

/** Every localStorage read here is best-effort: private/blocked storage must not break boot. */
const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Without persistence live mode simply never activates — not worth breaking the demo over.
  }
};

const readJson = <T>(key: string): T | null => {
  const raw = read(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const getDemoLiveState = (): DemoLiveState | null => {
  if (!isDemoBuild()) return null;
  const state = read(STATE_KEY);
  return state === 'pending' || state === 'active' ? state : null;
};

/** The real login already happened: IA and Metabase talk to the real backend. */
export const isDemoLiveActive = (): boolean => getDemoLiveState() === 'active' && !!read(TOKEN_KEY);

/** A real login was requested and hasn't completed: `/account/*` must pass through. */
export const isDemoLivePending = (): boolean => getDemoLiveState() === 'pending';

export const getDemoLiveToken = (): string | null => (isDemoLiveActive() ? read(TOKEN_KEY) : null);

export const getDemoLiveUser = (): DemoLiveUser | null => (isDemoLiveActive() ? readJson<DemoLiveUser>(USER_KEY) : null);

export const getDemoLiveFleets = (): DemoLiveFleet[] => (isDemoLiveActive() ? (readJson<DemoLiveFleet[]>(FLEETS_KEY) ?? []) : []);

export const getDemoLiveFleetId = (): string | null => (isDemoLiveActive() ? read(FLEET_KEY) : null);

/**
 * Auth headers for the real session, meant to override the demo's on one specific request.
 *
 * `CobliAxios.request` spreads `requestData.headers` **after** `getAuthHeaders()`, so what comes out
 * of here beats the fake token without needing a separate client.
 */
export const getDemoLiveAuthHeaders = (): Record<string, string> => {
  const token = getDemoLiveToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
    'cobli-api-sid': read(SID_KEY) || '',
  };
};

/**
 * GraphQL operations that live mode sends to the real backend.
 *
 * GraphQL is the one case the passthrough cannot decide by URL: every operation POSTs to the same
 * `/graphql`, so the discriminator has to be the operation name. `GraphQLRequester`'s `authLink`
 * reads this list, swaps in the real JWT and marks the request with `?_live=1`; everything not listed
 * here keeps the demo's headers and stays mocked.
 *
 * **Reads only, deliberately.** A mutation reaching the real backend would let the demo write to a
 * real fleet — create an expense, delete a record. Keeping mutations mocked means an edit in the
 * expenses table silently does nothing, which is the safe failure.
 *
 * The names are the operation names in `GraphQLTCORequester` (`query expenses`, `query
 * vehicleExpenses`, `query categories`), not the field names.
 */
const DEMO_LIVE_GRAPHQL_OPERATIONS = new Set(['expenses', 'vehicleExpenses', 'categories']);

export const isDemoLiveGraphQLOperation = (operationName: string | null | undefined): boolean =>
  !!operationName && isDemoLiveActive() && DEMO_LIVE_GRAPHQL_OPERATIONS.has(operationName);

/** Appends the passthrough marker, preserving any query params already present. */
export const withDemoLiveParam = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${DEMO_LIVE_PARAM}=1`;
};

const decode = (token: string | null): CobliJwtPayload | null => {
  if (!token) return null;
  try {
    return jwtDecode<CobliJwtPayload>(token);
  } catch {
    return null;
  }
};

/**
 * Did the JWT in the standard keys come from a real login rather than the demo seed?
 *
 * `helpers/jwt.ts#buildFakeJwt` signs with `iss: 'cobli-demo'`; that claim is what separates one
 * token from the other without validating a signature client-side.
 */
export const hasRealAccessTokenStored = (): boolean => {
  const payload = decode(read(AuthToken.ACCESS_TOKEN));
  return !!payload && payload.iss !== 'cobli-demo';
};

const herbieHost = (): string => Environment.getInstance().getEnvironment().herbieApiHost;

/** The same buffer `SessionManager` uses for the app token (`ACCESS_TOKEN_RENEWAL_BUFFER_SECONDS`). */
const TOKEN_RENEWAL_BUFFER_SECONDS = 120;

/** Seconds left on the real JWT. `null` when there is no readable token or it declares no `exp`. */
const secondsUntilExpiry = (): number | null => {
  const payload = decode(read(TOKEN_KEY));
  if (!payload?.exp) return null;
  return payload.exp - Math.floor(Date.now() / 1000);
};

/** The real JWT has expired — IA and Metabase will 401 until a refresh or a new login. */
export const isDemoLiveTokenExpired = (): boolean => {
  const remaining = secondsUntilExpiry();
  return remaining !== null && remaining <= 0;
};

export const getDemoLiveExpiresAt = (): Date | null => {
  const payload = decode(read(TOKEN_KEY));
  return payload?.exp ? new Date(payload.exp * 1000) : null;
};

/**
 * Reads `/account/session` with the real token and stores whatever comes back: fresh JWT, session
 * id, current fleet and accessible fleets.
 *
 * Serves two callers — the post-login capture (which wants the fleet list) and the refresh (which
 * wants the new JWT) — because it is literally the same request. The credential that authorizes the
 * refresh is the httpOnly refresh cookie sent by `credentials: 'include'`; the bearer riding along
 * may well be expired. Same mechanism `SessionManager` uses in the app.
 */
const syncDemoLiveSession = async (logPrefix: string): Promise<string | null> => {
  try {
    const response = await fetch(withDemoLiveParam(`${herbieHost()}/account/session`), {
      credentials: 'include',
      headers: getDemoLiveAuthHeaders(),
    });

    if (!response.ok) {
      logDemoLive(`${logPrefix} — /account/session respondeu ${response.status}. Sessão real não renovada.`);
      return null;
    }

    // `SessionAPI` (account/session/ApiTypes.ts): the full list is `user.accessible_fleets`;
    // `user.fleets` only carries the current fleet, at position 0.
    const session = (await response.json()) as SessionAPI;
    const accessible = session.user?.accessible_fleets ?? session.user?.fleets ?? [];
    const fleets = accessible.map(({ id, name }) => ({ id, name })).filter((fleet) => !!fleet.id);
    if (fleets.length > 0) write(FLEETS_KEY, JSON.stringify(fleets));
    if (session.session_id) write(SID_KEY, session.session_id);
    if (session.authentication_token) write(TOKEN_KEY, session.authentication_token);

    const currentFleetId = decode(session.authentication_token ?? null)?.fleetId;
    if (currentFleetId) write(FLEET_KEY, currentFleetId);

    logDemoLive(`${logPrefix} — sessão real sincronizada.`, {
      frotasAcessiveis: fleets.map((fleet) => fleet.name),
      tokenRenovado: !!session.authentication_token,
      expiraEm: getDemoLiveExpiresAt()?.toLocaleString('pt-BR') ?? 'desconhecido',
    });

    return session.authentication_token ?? null;
  } catch (err) {
    logDemoLive(`${logPrefix} FALHOU — não foi possível falar com /account/session.`, err);
    return null;
  }
};

/**
 * Deduplicated refresh: a single request in flight, like `SessionManager`'s `renewalPromise`.
 *
 * Without it, the chat and the Metabase embed waking up together with an expired token would fire
 * two concurrent refreshes and one would overwrite the other's token.
 */
let refreshPromise: Promise<string | null> | null = null;

export const refreshDemoLiveSession = async (): Promise<string | null> => {
  if (!isDemoLiveActive()) return null;
  if (refreshPromise) return refreshPromise;

  refreshPromise = syncDemoLiveSession('renovação');
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

/**
 * A real token ready to use, refreshed when it is about to expire.
 *
 * This is what consumers (Cobli IA and the Metabase embed) should call. `getDemoLiveToken()` still
 * exists for synchronous state reads in the UI, but makes no validity guarantee.
 */
export const getDemoLiveAccessToken = async (): Promise<string | null> => {
  const token = getDemoLiveToken();
  if (!token) return null;

  const remaining = secondsUntilExpiry();
  if (remaining !== null && remaining <= TOKEN_RENEWAL_BUFFER_SECONDS) {
    logDemoLive(remaining > 0 ? `token real expira em ${remaining}s — renovando antes de usar.` : `token real venceu há ${-remaining}s — tentando renovar.`);
    return (await refreshDemoLiveSession()) ?? token;
  }

  return token;
};

/** Like `getDemoLiveAuthHeaders`, but guaranteeing a valid token. */
export const getDemoLiveAuthHeadersFresh = async (): Promise<Record<string, string>> => {
  const token = await getDemoLiveAccessToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
    'cobli-api-sid': read(SID_KEY) || '',
  };
};

/**
 * Step 1: request the real login.
 *
 * Only clears the demo tokens and marks `pending`. With no valid session the `Router` falls through
 * to `/login`, the real login screen (`modules/login`) — which is the whole point: its Cognito flow
 * already handles the MFA challenge and remember-device, none of which needs reimplementing here.
 * The caller is responsible for navigating.
 */
export const beginDemoLiveLogin = (): void => {
  if (!isDemoBuild()) return;
  write(STATE_KEY, 'pending');
  try {
    localStorage.removeItem(AuthToken.ACCESS_TOKEN);
    localStorage.removeItem(AuthToken.REFRESH_TOKEN);
    logDemoLive('passo 1/3 — login real solicitado. Estado=pending, tokens do demo removidos. Indo para /login; /account/* agora passa direto ao backend real.');
  } catch {
    // If clearing fails the app keeps the demo session and the real login never happens.
    logDemoLive('passo 1/3 FALHOU — não foi possível remover os tokens do demo do localStorage. O app segue com a sessão fictícia e o login real não vai acontecer.');
  }
};

/**
 * Step 2: capture the real session and hand the shell back to the demo.
 *
 * Runs right after login: moves the real token into its own keys and fetches the accessible fleets.
 * The caller restores the demo tokens afterwards (at boot that is `startDemoWorker` itself), and
 * that restore is what stops the app from taking on the real identity.
 */
export const captureDemoLiveSession = async (): Promise<boolean> => {
  if (!isDemoBuild()) return false;

  const accessToken = read(AuthToken.ACCESS_TOKEN);
  const payload = decode(accessToken);
  if (!(accessToken && payload)) {
    logDemoLive('passo 2/3 pulado — não há JWT decodificável nas chaves padrão. O login real ainda não foi concluído.');
    return false;
  }
  if (payload.iss === 'cobli-demo') {
    logDemoLive('passo 2/3 pulado — o JWT nas chaves padrão é o falso do demo (iss=cobli-demo), não um token real.');
    return false;
  }

  write(TOKEN_KEY, accessToken);
  // The session id, not the refresh token: this value rides in the `cobli-api-sid` header, and
  // `syncDemoLiveSession` writes `session.session_id` to the same key — reading the refresh token
  // here meant the header carried a long-lived credential until the first renewal, then changed
  // meaning. The JWT already carries the claim.
  write(SID_KEY, payload.sessionId ?? '');
  write(USER_KEY, JSON.stringify({ email: payload.emailAddress, fullname: payload.fullName }));
  if (payload.fleetId) write(FLEET_KEY, payload.fleetId);
  // Mark active before fetching the fleets: `getDemoLiveAuthHeaders` requires the active state, and
  // the list is only menu garnish — if the call fails, live mode stays usable.
  write(STATE_KEY, 'active');

  const claimFleets = (payload.fleets ?? []).map(({ id, name }) => ({ id, name }));
  write(FLEETS_KEY, JSON.stringify(claimFleets));

  logDemoLive('passo 2/3 — sessão real capturada. Estado=active. IA e Metabase passam a usar este JWT; o shell volta a ser o do demo.', {
    usuario: payload.emailAddress,
    frotaAtual: payload.fleetId,
    frotasNoClaimDoJWT: claimFleets.length,
    expiraEm: payload.exp ? new Date(payload.exp * 1000).toLocaleString('pt-BR') : 'desconhecido',
  });

  await syncDemoLiveSession('passo 3/3');

  return true;
};

/**
 * Switches the real session's fleet.
 *
 * The same endpoint the app's own switcher uses (`POST /account/session/{fleetId}`), because the
 * fleet is the JWT's `fleetId` claim — switching fleets means re-minting the token. The difference
 * is that the response lands in the live-mode keys rather than the standard ones, which is why we do
 * not use `SessionManager`: its `parseAndStoreSession` would write to `cobli-access-token` and tear
 * down the demo shell.
 */
export const switchDemoLiveFleet = async (fleetId: string): Promise<void> => {
  if (!isDemoLiveActive()) {
    logDemoLive('troca de frota ignorada — modo real inativo.');
    return;
  }

  logDemoLive(`trocando a frota da sessão real para ${fleetId}…`);

  const response = await fetch(withDemoLiveParam(`${herbieHost()}/account/session/${fleetId}`), {
    method: 'POST',
    credentials: 'include',
    headers: getDemoLiveAuthHeaders(),
  });

  if (!response.ok) {
    logDemoLive(`troca de frota FALHOU — ${response.status}. O JWT anterior continua valendo.`);
    throw new Error(`Falha ao trocar a frota da sessão real (${response.status})`);
  }

  const session = (await response.json()) as { authentication_token?: string; session_id?: string };
  if (session.authentication_token) write(TOKEN_KEY, session.authentication_token);
  if (session.session_id) write(SID_KEY, session.session_id);
  write(FLEET_KEY, fleetId);

  logDemoLive(`frota trocada. JWT remintado para ${fleetId}; recarregando para IA e Metabase pegarem o token novo.`, {
    tokenRemintado: !!session.authentication_token,
  });
};

export const exitDemoLive = (): void => {
  try {
    for (const key of DEMO_LIVE_KEYS) {
      localStorage.removeItem(key);
    }
    logDemoLive('modo real encerrado — chaves da sessão real removidas. IA e Metabase voltam a ser mockados.');
  } catch {
    // Nothing to do: without localStorage the state was not persisting anyway.
  }
};
